import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Bold,
  Italic,
  Underline,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Eraser,
} from 'lucide-react';

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};

  .req {
    color: ${(p) => p.theme.error};
    margin-left: 0.15rem;
  }
`;

const Frame = styled.div<{ $error?: boolean }>`
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${(p) => (p.$error ? p.theme.error : p.theme.primary)};
    box-shadow: 0 0 0 3px ${(p) => (p.$error ? p.theme.error : p.theme.primary)}22;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.3rem 0.4rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};
  flex-wrap: wrap;

  .sep {
    width: 1px;
    height: 18px;
    background: ${(p) => p.theme.border};
    margin: 0 0.2rem;
  }
`;

const ToolButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  cursor: pointer;
  background: ${(p) => (p.$active ? p.theme.primary : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : p.theme.textSecondary)};
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${(p) => (p.$active ? p.theme.primaryDark : p.theme.lightGray)};
    color: ${(p) => (p.$active ? '#fff' : p.theme.textPrimary)};
  }
`;

const Editable = styled.div<{ $minHeight?: number }>`
  min-height: ${(p) => p.$minHeight ?? 120}px;
  max-height: 360px;
  overflow-y: auto;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  outline: none;
  line-height: 1.55;

  &:empty:before {
    content: attr(data-placeholder);
    color: ${(p) => p.theme.textMuted};
  }

  p {
    margin: 0 0 0.5rem;
  }
  h3 {
    font-size: 1rem;
    font-weight: 700;
    margin: 0.2rem 0 0.5rem;
    color: ${(p) => p.theme.textPrimary};
  }
  ul,
  ol {
    margin: 0 0 0.5rem;
    padding-left: 1.4rem;
  }
  a {
    color: ${(p) => p.theme.primary};
    text-decoration: underline;
  }
`;

const LinkBar = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  padding: 0.5rem;
  border-top: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};

  input {
    flex: 1;
    padding: 0.45rem 0.6rem;
    font-size: 0.85rem;
    font-family: inherit;
    color: ${(p) => p.theme.textPrimary};
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius};

    &:focus {
      outline: none;
      border-color: ${(p) => p.theme.primary};
    }
  }
`;

const LinkAction = styled.button<{ $primary?: boolean }>`
  padding: 0.45rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => (p.$primary ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$primary ? p.theme.primary : 'transparent')};
  color: ${(p) => (p.$primary ? '#fff' : p.theme.textSecondary)};
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

/** innerHTML values a browser leaves behind for a visually empty editor. */
const EMPTY_HTML = ['', '<br>', '<div><br></div>', '<p><br></p>', '<p></p>'];

interface RichTextInputProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  minHeight?: number;
  containerStyle?: React.CSSProperties;
}

interface ActiveState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  ul: boolean;
  ol: boolean;
  h3: boolean;
}

const EMPTY_ACTIVE: ActiveState = {
  bold: false,
  italic: false,
  underline: false,
  ul: false,
  ol: false,
  h3: false,
};

/**
 * Minimal dependency-free rich-text editor producing HTML. Uses a contentEditable
 * surface plus document.execCommand for basic formatting (bold/italic/underline,
 * subtitle, lists, links). The value is the editor's innerHTML.
 *
 * Kept internally uncontrolled to avoid caret jumps: the incoming [value] is only
 * written back to the DOM when it diverges from the current innerHTML (our own
 * edits keep them in sync, so typing never triggers a rewrite).
 */
export const RichTextInput: React.FC<RichTextInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  required,
  minHeight,
  containerStyle,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const [active, setActive] = useState<ActiveState>(EMPTY_ACTIVE);
  const [linkOpen, setLinkOpen] = useState<boolean>(false);
  const [linkUrl, setLinkUrl] = useState<string>('');

  useEffect(() => {
    const el = ref.current;
    if (el && (value ?? '') !== el.innerHTML) {
      el.innerHTML = value ?? '';
    }
  }, [value]);

  const emit = useCallback((): void => {
    const el = ref.current;
    if (!el) return;
    const html = el.innerHTML;
    onChange(EMPTY_HTML.includes(html.trim().toLowerCase()) ? '' : html);
  }, [onChange]);

  const refreshActive = useCallback((): void => {
    try {
      const block = document.queryCommandValue('formatBlock') || '';
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        ul: document.queryCommandState('insertUnorderedList'),
        ol: document.queryCommandState('insertOrderedList'),
        h3: /h3/i.test(block),
      });
    } catch {
      setActive(EMPTY_ACTIVE);
    }
  }, []);

  const exec = (command: string, arg?: string): void => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
    refreshActive();
  };

  const toggleHeading = (): void => {
    ref.current?.focus();
    const block = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    document.execCommand('formatBlock', false, block === 'h3' ? '<p>' : '<h3>');
    emit();
    refreshActive();
  };

  const openLink = (): void => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
    setLinkUrl('');
    setLinkOpen(true);
  };

  const applyLink = (): void => {
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!url) return;
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    const href = /^(https?:\/\/|mailto:)/i.test(url) ? url : `https://${url}`;
    document.execCommand('createLink', false, href);
    emit();
  };

  const prevent = (fn: () => void) => (e: React.MouseEvent) => {
    // Keep the current selection: stop the toolbar from stealing focus.
    e.preventDefault();
    fn();
  };

  return (
    <Group style={containerStyle}>
      {label && (
        <Label>
          {label}
          {required && <span className="req">*</span>}
        </Label>
      )}
      <Frame $error={!!error}>
        <Toolbar>
          <ToolButton type="button" title="Negrito" $active={active.bold} onMouseDown={prevent(() => exec('bold'))}>
            <Bold size={15} />
          </ToolButton>
          <ToolButton type="button" title="Itálico" $active={active.italic} onMouseDown={prevent(() => exec('italic'))}>
            <Italic size={15} />
          </ToolButton>
          <ToolButton type="button" title="Sublinhado" $active={active.underline} onMouseDown={prevent(() => exec('underline'))}>
            <Underline size={15} />
          </ToolButton>
          <span className="sep" />
          <ToolButton type="button" title="Subtítulo" $active={active.h3} onMouseDown={prevent(toggleHeading)}>
            <Heading3 size={15} />
          </ToolButton>
          <ToolButton type="button" title="Lista" $active={active.ul} onMouseDown={prevent(() => exec('insertUnorderedList'))}>
            <List size={15} />
          </ToolButton>
          <ToolButton type="button" title="Lista numerada" $active={active.ol} onMouseDown={prevent(() => exec('insertOrderedList'))}>
            <ListOrdered size={15} />
          </ToolButton>
          <span className="sep" />
          <ToolButton type="button" title="Inserir link" $active={linkOpen} onMouseDown={prevent(openLink)}>
            <Link2 size={15} />
          </ToolButton>
          <ToolButton type="button" title="Limpar formatação" onMouseDown={prevent(() => exec('removeFormat'))}>
            <Eraser size={15} />
          </ToolButton>
        </Toolbar>
        <Editable
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          contentEditable
          suppressContentEditableWarning
          $minHeight={minHeight}
          data-placeholder={placeholder}
          onInput={() => {
            emit();
            refreshActive();
          }}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          onFocus={refreshActive}
          onBlur={emit}
        />
        {linkOpen && (
          <LinkBar>
            <input
              value={linkUrl}
              autoFocus
              placeholder="https://exemplo.com"
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === 'Escape') {
                  setLinkOpen(false);
                }
              }}
            />
            <LinkAction type="button" $primary onClick={applyLink}>
              Aplicar
            </LinkAction>
            <LinkAction type="button" onClick={() => setLinkOpen(false)}>
              Cancelar
            </LinkAction>
          </LinkBar>
        )}
      </Frame>
      {error && <ErrorText>{error}</ErrorText>}
    </Group>
  );
};

export default RichTextInput;
